import { useWeb3React } from "@web3-react/core";
import { get, getDatabase, ref, set } from "firebase/database";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";

import { ConfirmSVG } from "../../assets";
import { Button } from "../../components/Button/Button";
import { SnackMessage } from "../../components/MessageBar/SnackMessage";
import { Modal } from "../../components/Modal/Modal";
import { Text } from "../../components/Text/Text";
import { useCosmJsProvider } from "../../contexts";
import { useNetworks } from "../../hooks";
import "./LegalPopup.scss";

export const LegalPopup = () => {
  const db = getDatabase();

  const { enqueueSnackbar } = useSnackbar();

  const { networks } = useNetworks();
  const { address: evmAddress } = networks.evm;
  const { address: cosmosAddress } = networks.cosmos;
  const { address: solanaAddress, signer: solanaSigner } = networks.solana;

  const { connector: cosmJsConnector } = useCosmJsProvider();
  const { connector: evmConnector } = useWeb3React();

  const [isModalVisible, setModalVisible] = useState(false);
  const [unconfirmedLegals, setUnconfirmedLegals] = useState<
    Partial<Record<"evm" | "cosmos" | "solana", boolean>>
  >({});

  useEffect(() => {
    const fetchLegalData = async () => {
      if (!evmAddress && !cosmosAddress && !solanaAddress) {
        return;
      }

      const tempUnconfirmedLegals: Record<string, boolean> = {};

      if (evmAddress) {
        const evmTermsOfUse = await get(
          ref(db, `users/${evmAddress}/consents/terms_of_use`),
        );

        const evmPrivacyPolicy = await get(
          ref(db, `users/${evmAddress}/consents/privacy_policy`),
        );

        if (!evmTermsOfUse.exists() || !evmPrivacyPolicy.exists()) {
          tempUnconfirmedLegals.evm = true;
        }
      }

      if (cosmosAddress) {
        const cosmosTermsOfUse = await get(
          ref(db, `users/${cosmosAddress}/consents/terms_of_use`),
        );

        const cosmosPrivacyPolicy = await get(
          ref(db, `users/${cosmosAddress}/consents/privacy_policy`),
        );

        if (!cosmosTermsOfUse.exists() || !cosmosPrivacyPolicy.exists()) {
          tempUnconfirmedLegals.cosmos = true;
        }
      }

      if (solanaAddress) {
        const solanaTermsOfUse = await get(
          ref(db, `users/${solanaAddress}/consents/terms_of_use`),
        );

        const solanaPrivacyPolicy = await get(
          ref(db, `users/${solanaAddress}/consents/privacy_policy`),
        );

        if (!solanaTermsOfUse.exists() || !solanaPrivacyPolicy.exists()) {
          tempUnconfirmedLegals.solana = true;
        }
      }

      if (
        tempUnconfirmedLegals.evm ||
        tempUnconfirmedLegals.cosmos ||
        tempUnconfirmedLegals.solana
      ) {
        setUnconfirmedLegals(tempUnconfirmedLegals);
        setModalVisible(true);
      }
    };

    fetchLegalData();
  }, [db, evmAddress, cosmosAddress, solanaAddress]);

  const confirm = async () => {
    try {
      if (unconfirmedLegals.evm) {
        await Promise.all([
          set(ref(db, `users/${evmAddress}/consents/terms_of_use`), true),
          set(ref(db, `users/${evmAddress}/consents/privacy_policy`), true),
        ]);
      }
      if (unconfirmedLegals.cosmos) {
        await Promise.all([
          set(ref(db, `users/${cosmosAddress}/consents/terms_of_use`), true),
          set(ref(db, `users/${cosmosAddress}/consents/privacy_policy`), true),
        ]);
      }
      if (unconfirmedLegals.solana) {
        await Promise.all([
          set(ref(db, `users/${solanaAddress}/consents/terms_of_use`), true),
          set(ref(db, `users/${solanaAddress}/consents/privacy_policy`), true),
        ]);
      }
    } catch (err: any) {
      enqueueSnackbar(
        <SnackMessage
          message={{
            text: "Confirmation failed!",
            variant: "error",
          }}
        />,
      );
      cancel();
    } finally {
      setModalVisible(false);
    }
  };

  const cancel = () => {
    setModalVisible(false);
    if (unconfirmedLegals.evm) {
      if (evmConnector?.deactivate) {
        evmConnector.deactivate();
      } else {
        evmConnector.resetState();
      }
      localStorage.removeItem("lastEvmConnector");
    }

    if (unconfirmedLegals.cosmos) {
      if (cosmJsConnector?.deactivate) {
        cosmJsConnector.deactivate();
      }
      localStorage.removeItem("lastCosmosConnector");
    }

    if (unconfirmedLegals.solana) {
      solanaSigner.disconnect();
      localStorage.removeItem("lastSolanaConnector");
    }
  };

  return (
    <>
      {isModalVisible && (
        <Modal onCloseClick={() => {}} id="legal-modal">
          <div className="legal-popup">
            <div className="legal-popup__title-container">
              <Text className="legal-popup__title" text="Confirmation" />
              <ConfirmSVG className="legal-popup__icon" />
            </div>
            <div className="legal-popup__container-confirmation-item legal-popup__container-confirmation-item-left">
              <label
                htmlFor="consent1"
                className="legal-popup__confirmation-text"
              >
                Confirmation for address:{" "}
                <span className="legal-popup__address">
                  {`${unconfirmedLegals.evm ? evmAddress : ""} ${
                    unconfirmedLegals.cosmos ? cosmosAddress : ""
                  } ${unconfirmedLegals.solana ? solanaAddress : ""}`}
                </span>
              </label>
            </div>
            <div className="legal-popup__container-confirmation-item">
              <label
                htmlFor="consent1"
                className="legal-popup__confirmation-text"
              >
                I hereby declare that I have read and understood the{" "}
                <a
                  href="https://mygateway.io/terms_of_use.pdf"
                  rel="noreferrer"
                  target="_blank"
                >
                  Terms of Use
                </a>{" "}
                and{" "}
                <a
                  href="https://mygateway.io/privacy_policy.pdf"
                  rel="noreferrer"
                  target="_blank"
                >
                  Privacy Policy
                </a>{" "}
                applicable on the Platform and I accept to be bound by their
                provisions.
              </label>
            </div>

            <footer className="legal-popup__footer">
              <Button onClick={cancel} variant="OUTLINED">
                Disconnect
              </Button>
              <Button onClick={confirm} variant="PRIMARY">
                Confirm
              </Button>
            </footer>
          </div>
        </Modal>
      )}
    </>
  );
};
